import { useState, useEffect, useCallback } from 'react';
import { SectionWrapper } from '../SectionWrapper';
import { Store } from 'lucide-react';
import { VendorSetupSection } from '../sections/VendorSetupSection';
import { COGSVendorTrainingStep1 } from '../phase2/COGSVendorTrainingStep1';
import { COGSVendorTrainingStep2 } from '../phase2/COGSVendorTrainingStep2';
import { COGSVendorTrainingStep3Merged } from '../phase2/COGSVendorTrainingStep3Merged';
import { COGSVendorTrainingStep4 } from '../phase2/COGSVendorTrainingStep4';
import { COGSVendorTrainingStep4Summary } from '../phase2/COGSVendorTrainingStep4Summary';
import { COGSVendorTrainingStep5 } from '../phase2/COGSVendorTrainingStep5';
import { COGSVendorTrainingStep5ManualCosts } from '../phase2/COGSVendorTrainingStep5ManualCosts';
import { COGSVendorTrainingStep6 } from '../phase2/COGSVendorTrainingStep6';
import { COGSVendorTrainingStep7 } from '../phase2/COGSVendorTrainingStep7';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';

const STORAGE_KEY = 'menubrain_cogs_training_progress';

const TOTAL_STEPS = 7; // Step 1: Vendor Setup, Step 2: Select Vendors, Step 3: Upload & Review, Step 4: Map Line Items, Step 5: Fill Missing Costs, Step 6: Review Mappings, Step 7: Complete Training

interface COGSVendorTrainingCombinedProps {
  onComplete?: () => void | Promise<void>;
  onSkip?: () => void | Promise<void>;
  onSaveAndReturn?: () => void | Promise<void>;
}

export const COGSVendorTrainingCombined = ({ onComplete, onSkip, onSaveAndReturn }: COGSVendorTrainingCombinedProps) => {
  const { completeSection, state } = useOnboarding();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
  const [currentVendorIndex, setCurrentVendorIndex] = useState(0);
  const [vendors, setVendors] = useState<any[]>([]);
  const [editMappingParams, setEditMappingParams] = useState<{ vendorId: string; clusterId?: string } | undefined>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasPendingProcessing, setHasPendingProcessing] = useState(false);
  const [vendorRefreshKey, setVendorRefreshKey] = useState(0);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [savedProgress, setSavedProgress] = useState<{ step: number; vendors: string[] } | null>(null);

  // Check for saved progress on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.step && parsed.step > 1) {
          setSavedProgress(parsed);
          setShowResumeDialog(true);
        }
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Persist progress on step/vendor changes
  const saveProgress = useCallback((step: number, vendors: string[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, vendors }));
    } catch { /* ignore storage errors */ }
  }, []);

  useEffect(() => {
    if (currentStep > 1) {
      saveProgress(currentStep, selectedVendors);
    }
  }, [currentStep, selectedVendors, saveProgress]);

  useEffect(() => {
    if (selectedVendors.length > 0 && currentStep > 1) {
      loadVendors();
    }
  }, [selectedVendors, currentStep]);

  // If we're on step 3 (invoice upload) but have no selected vendors, go back to step 2
  useEffect(() => {
    if (currentStep === 3 && selectedVendors.length === 0) {
      setCurrentStep(2);
    }
  }, [currentStep, selectedVendors.length]);

  const loadVendors = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const { data } = await supabase
        .from('vendors')
        .select('*')
        .eq('company_id', companyId)
        .in('id', selectedVendors);

      if (data) {
        setVendors(data);
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const handleNext = async () => {
    // Block navigation if processing is active or pending
    if (isProcessing || hasPendingProcessing) {
      return;
    }
    
    if (currentStep === 1) {
      // After vendor setup, mark vendors as completed and move to training
      try {
        await completeSection('vendors');
        // Increment refresh key to force vendor list reload
        setVendorRefreshKey(prev => prev + 1);
        setCurrentStep(2);
      } catch (error) {
        console.error('Error completing vendor setup:', error);
      }
    } else if (currentStep < TOTAL_STEPS) {
      // Ensure we have selected vendors before moving to step 3 (invoice upload)
      if (currentStep === 2 && selectedVendors.length === 0) {
        console.warn('Cannot proceed to step 3 without selected vendors');
        return;
      }
      // Move to next step
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      
      if (nextStep === TOTAL_STEPS) {
        if (!selectedVendors || selectedVendors.length === 0) {
          console.warn('Cannot proceed to final step without selected vendors');
          setCurrentStep(2);
          return;
        }
      }
    } else {
      // Complete the training - clear saved progress
      localStorage.removeItem(STORAGE_KEY);
      if (onComplete) {
        await onComplete();
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      // If going back to step 1 (vendor setup), increment refresh key
      // so that when user returns to step 2, vendors are reloaded from DB
      if (currentStep === 2) {
        setVendorRefreshKey(prev => prev + 1);
      }
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    if (onSkip) {
      await onSkip();
    }
  };

  const getStepName = () => {
    switch (currentStep) {
      case 1: return 'Vendor Setup';
      case 2: return 'Select Vendors';
      case 3: return 'Upload & Review';
      case 4: return 'Map Line Items';
      case 5: return 'Fill Missing Costs';
      case 6: return 'Review Mappings';
      case 7: return 'Complete Training';
      default: return '';
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <VendorSetupSection />;
      case 2:
        return (
          <COGSVendorTrainingStep1
            key={`vendor-select-${vendorRefreshKey}`} // Force remount to reload vendors from DB when returning from step 1
            onNext={handleNext}
            onVendorsSelected={(vendors) => {
              setSelectedVendors(vendors);
            }}
          />
        );
      case 3:
        return (
          <COGSVendorTrainingStep3Merged
            key={`invoice-upload-${selectedVendors.join('-')}`}
            onNext={handleNext}
            onPrevious={handlePrevious}
            selectedVendors={selectedVendors}
            currentVendorIndex={currentVendorIndex}
            onVendorIndexChange={setCurrentVendorIndex}
            editMappingParams={editMappingParams}
            onEditMappingCleared={() => setEditMappingParams(undefined)}
            onProcessingStateChange={(processing, hasPending) => {
              setIsProcessing(processing);
              setHasPendingProcessing(hasPending);
            }}
          />
        );
      case 4:
        return (
          <COGSVendorTrainingStep5
            onNext={handleNext}
            onPrevious={handlePrevious}
            selectedVendors={selectedVendors}
            onEditMapping={(vendorId, clusterId) => {
              const vendorIndex = selectedVendors.indexOf(vendorId);
              if (vendorIndex >= 0) {
                setCurrentVendorIndex(vendorIndex);
                setEditMappingParams({ vendorId, clusterId });
                setCurrentStep(3);
              }
            }}
          />
        );
      case 5:
        return (
          <COGSVendorTrainingStep5ManualCosts
            onNext={handleNext}
            onPrevious={handlePrevious}
            selectedVendors={selectedVendors}
          />
        );
      case 6:
        return (
          <COGSVendorTrainingStep6
            onNext={handleNext}
            onPrevious={handlePrevious}
            selectedVendors={selectedVendors}
            onEditMapping={(vendorId, clusterId) => {
              const vendorIndex = selectedVendors.indexOf(vendorId);
              if (vendorIndex >= 0) {
                setCurrentVendorIndex(vendorIndex);
                setEditMappingParams({ vendorId, clusterId });
                setCurrentStep(4);
              }
            }}
          />
        );
      case 7:
        return (
          <COGSVendorTrainingStep7
            onNext={handleNext}
            onPrevious={handlePrevious}
            selectedVendors={selectedVendors}
            onComplete={async () => {
              if (onComplete) await onComplete();
            }}
          />
        );
      default:
        return null;
    }
  };

  const handleResume = () => {
    if (savedProgress) {
      const targetStep = Math.min(savedProgress.step, TOTAL_STEPS);
      setCurrentStep(targetStep);
      if (savedProgress.vendors.length > 0) {
        setSelectedVendors(savedProgress.vendors);
      }
    }
    setShowResumeDialog(false);
  };

  const handleStartOver = () => {
    localStorage.removeItem(STORAGE_KEY);
    setCurrentStep(1);
    setSelectedVendors([]);
    setShowResumeDialog(false);
  };

  const resumeDialog = (
    <Dialog open={showResumeDialog} onOpenChange={setShowResumeDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resume Training?</DialogTitle>
          <DialogDescription>
            You have saved progress from a previous session (Step {savedProgress?.step} of {TOTAL_STEPS}).
            Would you like to resume where you left off or start from the beginning?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleStartOver}>
            Start from Step 1
          </Button>
          <Button onClick={handleResume}>
            Resume (Step {savedProgress?.step})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (currentStep === 1) {
    return (
      <>
      {resumeDialog}
      <VendorSetupSection
        onNext={async () => {
          // In Phase 2, we don't want to call completeSection from Phase 1 context
          // because it triggers navigation. The vendors are already saved in the database
          // by VendorSetupSection's handleNext, so we just need to move to step 2.
          // Increment refresh key to force COGSVendorTrainingStep1 to remount and reload vendors
          setVendorRefreshKey(prev => prev + 1);
          setCurrentStep(2);
        }}
      />
      </>
    );
  }

  return (
    <>
    {resumeDialog}
    <SectionWrapper
      title="COGS Vendor Training"
      description="Train AI to recognize and categorize vendor invoices."
      icon={<Store className="h-6 w-6" />}
      onNext={currentStep === 2 && selectedVendors.length === 0 ? undefined : handleNext}
      onPrevious={handlePrevious}
      onSkip={handleSkip}
      onSaveAndReturn={onSaveAndReturn}
      canSkip
      nextLabel={currentStep === TOTAL_STEPS ? 'Complete' : 'Continue to Next Step'}
      isNextDisabled={currentStep === 2 && selectedVendors.length === 0}
    >
      <div className="space-y-6">
        {/* Progress indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              Step {currentStep} of {TOTAL_STEPS}: {getStepName()}
            </span>
            <span className="text-sm text-muted-foreground">
              {Math.round((currentStep / TOTAL_STEPS) * 100)}% complete
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all"
              style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
            />
          </div>
        </div>

        {/* Step content */}
        {renderStep()}
      </div>
    </SectionWrapper>
    </>
  );
};

